import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Award, Trophy, MapPin, Quote, Search, Truck, Play, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import DisclosureBanner from "../components/DisclosureBanner";
import { fileUrl, videoSrc, isYouTube, PlayBadge, VideoModal, ShareButtons } from "../components/DriverMedia";

export default function DriverOfTheWeek() {
  const { id } = useParams();
  const [current, setCurrent] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [q, setQ] = useState("");
  const [playing, setPlaying] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, all] = await Promise.all([api.get("/drivers/current"), api.get("/drivers")]);
        setCurrent(c.data);
        setDrivers(all.data || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    setNotFound(false);
    if (!id) { setFeatured(null); return; }
    api.get(`/drivers/${id}`).then((r) => setFeatured(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const shown = id ? featured : current;
  const src = videoSrc(shown);
  const archive = drivers.filter((d) => d.id !== shown?.id && (!q || d.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <VideoModal driver={playing} onClose={() => setPlaying(null)} />
      <DisclosureBanner />
      <section className="py-14 md:py-20 gradient-radial grain-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> Driver Recognition</div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white display">Driver of the Week</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Celebrating the professionals whose safety, service, and dedication set the standard for our team.</p>
        </div>
      </section>

      {shown ? (
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {id && (
              <Link to="/driver-of-the-week" data-testid="driver-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 mb-6"><ArrowLeft className="w-4 h-4" /> All recognitions</Link>
            )}
            <div className="grid md:grid-cols-5 gap-8">
              <div className="md:col-span-2">
                <div
                  data-testid="driver-current-media"
                  className={`relative group ${src ? "cursor-pointer" : ""}`}
                  onClick={() => src && setPlaying(shown)}
                >
                  <div className="absolute -inset-3 stripe-amber rounded-2xl opacity-30 blur-sm" />
                  {shown.photo_file_id ? (
                    <img data-testid="driver-current-photo" src={fileUrl(shown.photo_file_id)} alt={shown.name} className="relative rounded-2xl border border-slate-700 w-full h-[440px] object-cover" />
                  ) : src && !isYouTube(src) ? (
                    <video src={`${src}#t=0.5`} muted playsInline preload="metadata" className="relative rounded-2xl border border-slate-700 w-full h-[440px] object-cover bg-slate-900" />
                  ) : (
                    <div className="relative rounded-2xl border border-slate-700 w-full h-[440px] bg-slate-900 flex items-center justify-center">
                      <Truck className="w-20 h-20 text-slate-700" />
                    </div>
                  )}
                  {src && <div className="absolute inset-0 rounded-2xl overflow-hidden"><PlayBadge /></div>}
                  {shown.is_current && (
                    <div className="absolute top-4 left-4 bg-amber-500 text-slate-950 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Current Winner</div>
                  )}
                </div>
              </div>
              <div className="md:col-span-3">
                <div className="text-xs uppercase tracking-widest text-slate-500">Week of {shown.week_of}</div>
                <h2 data-testid="driver-current-name" className="mt-2 text-4xl md:text-5xl font-extrabold uppercase display text-white">{shown.name}</h2>
                {shown.route_zone && (
                  <div className="mt-2 flex items-center gap-2 text-amber-400 text-sm"><MapPin className="w-4 h-4" /> {shown.route_zone}</div>
                )}
                <div className="mt-6 text-xs uppercase tracking-widest text-slate-500 font-semibold">Achievements</div>
                <p className="mt-2 text-slate-200 leading-relaxed whitespace-pre-line">{shown.achievements}</p>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  {src && (
                    <button data-testid="driver-current-watch-btn" onClick={() => setPlaying(shown)} className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition-colors w-fit">
                      <Play className="w-4 h-4" fill="currentColor" /> Watch the video
                    </button>
                  )}
                  <ShareButtons driver={shown} />
                </div>
                {shown.safety_record && (
                  <>
                    <div className="mt-6 text-xs uppercase tracking-widest text-slate-500 font-semibold">Safety Record</div>
                    <p className="mt-2 text-slate-200 leading-relaxed">{shown.safety_record}</p>
                  </>
                )}
                {shown.manager_quote && (
                  <div className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
                    <Quote className="w-6 h-6 text-amber-400" />
                    <p className="mt-2 italic text-slate-200 leading-relaxed">"{shown.manager_quote}"</p>
                    <div className="mt-3 text-xs uppercase tracking-widest text-amber-400 font-semibold">— Operations Team</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16">
          <div className="max-w-2xl mx-auto text-center">
            <Trophy className="w-14 h-14 text-slate-700 mx-auto" />
            <p data-testid="driver-empty-message" className="mt-4 text-slate-400">
              {notFound ? "We couldn't find that recognition." : "Our next Driver of the Week recognition will be announced soon."}
            </p>
            {notFound && <Link to="/driver-of-the-week" className="mt-4 inline-block text-amber-400 text-sm">View all recognitions</Link>}
          </div>
        </section>
      )}

      <section className="py-14 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h3 className="text-2xl md:text-3xl font-bold uppercase display text-white">Recognition Archive</h3>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="driver-archive-search" placeholder="Search by name..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-slate-950 border-slate-700 text-white w-64" />
            </div>
          </div>
          {archive.length === 0 ? (
            <p className="mt-8 text-slate-500 text-sm">No previous recognitions yet.</p>
          ) : (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {archive.map((d) => (
                <Link key={d.id} to={`/driver-of-the-week/${d.id}`} data-testid={`driver-archive-card-${d.id}`} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden group hover:border-amber-500/50 transition-colors">
                  <div className="aspect-square bg-slate-950 relative">
                    {d.photo_file_id ? (
                      <img src={fileUrl(d.photo_file_id)} alt={d.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Truck className="w-12 h-12 text-slate-700" /></div>
                    )}
                    {videoSrc(d) && <PlayBadge size="sm" />}
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Week of {d.week_of}</div>
                    <div className="text-white font-bold display uppercase mt-1">{d.name}</div>
                    {d.route_zone && <div className="text-xs text-slate-400 mt-0.5">{d.route_zone}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
